import {useMemo} from 'react';
import useAuth from './useAuth';
import {
  getCompanyConfig,
  CompanyConfig,
  CompanyLabels,
  FeatureFlags,
} from '../config/company-types';
import {LayoutConfig} from 'config/types';
import {defaultLayoutConfig} from 'config/company-types/base.config';

export interface UseCompanyConfigReturn {
  /** Full company configuration object */
  config: CompanyConfig;
  /** UI labels for the current company type */
  labels: CompanyLabels;
  /** Feature flags for the current company type */
  features: FeatureFlags;
  /** Check if a specific feature is enabled */
  hasFeature: (feature: keyof FeatureFlags) => boolean;
  /** Get a specific label value */
  getLabel: (key: keyof CompanyLabels) => string;
  /** Convenience: is this a property manager company */
  isPropertyManager: boolean;
  /** Convenience: is this a service provider company */
  isServiceProvider: boolean;
  /** Convenience: is this a delivery manager company */
  isDeliveryManager: boolean;
  /** Convenience: is this an MSP provider company */
  isMSPProvider: boolean;
  /** Convenience: is this a pickup/dropoff company */
  isPickupDropoff: boolean;
  /** Convenience: is this an insurance adjuster company */
  isInsuranceAdjuster: boolean;
  /** Layout configuration for the current company type */
  layoutConfig: LayoutConfig;
}

/**
 * Hook to access company-specific configuration
 *
 * @example
 * ```tsx
 * function OrderList() {
 *   const { labels, hasFeature } = useCompanyConfig();
 *
 *   return (
 *     <div>
 *       <h1>{labels.orders}</h1>
 *       {hasFeature('hasLandlordFilter') && <LandlordFilter />}
 *     </div>
 *   );
 * }
 * ```
 */
export const useCompanyConfig = (): UseCompanyConfigReturn => {
  const {currentUser} = useAuth();
  const companyType = currentUser?.company?.type;

  return useMemo(() => {
    const config = getCompanyConfig(companyType);

    return {
      config,
      labels: config.labels,
      features: config.features,
      layoutConfig: config.layoutConfig ?? defaultLayoutConfig,
      hasFeature: (feature: keyof FeatureFlags) => config.features[feature],
      getLabel: (key: keyof CompanyLabels) => config.labels[key],
      // Convenience boolean flags for backwards compatibility
      isPropertyManager: companyType === 'property_manager',
      isServiceProvider: companyType === 'service_provider',
      isDeliveryManager: companyType === 'delivery_manager',
      isMSPProvider: companyType === 'msp_provider',
      isPickupDropoff: companyType === 'pickup_dropoff',
      isInsuranceAdjuster: companyType === 'insurance_adjuster',
    };
  }, [companyType]);
};

export default useCompanyConfig;
